import { Badge } from '@/components/ui/badge'
import { ArtworkStatus, OrderStatus } from '@/types/entities'
import { cn } from '@/lib/utils'

interface StatusBadgeProps {
  status: ArtworkStatus | OrderStatus
  size?: 'sm' | 'md' | 'lg'
  /**
   * A pending artwork a manager has held. It is still PENDING in the data, but to the artist it is
   * a different thing from one waiting its turn: this one will not move until they do something.
   */
  needsReview?: boolean | null
}

const artworkStatusConfig = {
  [ArtworkStatus.DRAFT]: {
    label: 'Draft',
    className: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  },
  [ArtworkStatus.PENDING]: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  },
  [ArtworkStatus.PUBLISHED]: {
    label: 'Published',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
  },
}

const orderStatusConfig = {
  [OrderStatus.PENDING]: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  },
  [OrderStatus.PARTIAL]: {
    label: 'Partial',
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  },
  [OrderStatus.FULFILLED]: {
    label: 'Fulfilled',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
  },
}

const needsReviewConfig = {
  label: 'Needs review',
  className: 'bg-red-100 text-red-800 hover:bg-red-100',
}

export function StatusBadge({ status, size = 'md', needsReview }: StatusBadgeProps) {
  const config =
    needsReview && status === ArtworkStatus.PENDING
      ? needsReviewConfig
      : status in ArtworkStatus
        ? artworkStatusConfig[status as ArtworkStatus]
        : orderStatusConfig[status as OrderStatus]

  const sizeClass = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1',
  }

  return (
    <Badge className={cn(config.className, sizeClass[size])} variant="secondary">
      {config.label}
    </Badge>
  )
}

